import * as React from 'react';
import { FrontLayout } from 'client/ui/layout/FrontLayout';
import clx from 'classnames';
import { Link, RouteComponentProps } from 'react-router-dom';
import { Col, Typography, message, Alert } from 'antd';
import { getWidgetInputLayout } from 'client/components/schema/widget/helper/getWidgetInputLayout';
import { useApiQuery } from 'client/core/network/hooks/useApiQuery';
import { AlertPage } from 'client/ui/alert/AlertPage';
import { getNetworkErrorMessage } from 'client/core/network/errors/getNetworkErrorMessage';
import { LoadingPage } from 'client/ui/loader/LoadingPage';
import { Formik, FormikProps } from 'formik';
import {
  ICreateRequestDto,
  createRequestValidator
} from 'common/dto/RequestDto';
import { FormikForm } from 'client/ui/form/FormikForm';
import { schemaToValidator } from 'client/core/validation/schemaToValidator';
import { useApiMutation } from 'client/core/network/hooks/useApiMutation';
import { RequestApi } from 'client/components/schema/requests/RequestApi';
import { RequestSendSuccessMessage } from '../../../../components/schema/requests/success/RequestSendSuccessMessage';
import { FormErrorsNotification } from 'client/ui/form/errors/FormErrorsNotification';
import {
  useCurrentCitizen,
  useCurrentUser,
  usePermissions
} from 'client/components/auth/AuthModule';
import { RequestState } from 'common/schema/request/Request';
import { ContextualRequestBag } from 'common/schema/contextual-value/ContextualValue';
import moment from 'moment';
import { useBreadcrumbItem } from 'client/core/router/breadcrumb/BreadcrumbContext';
import { PreventNavigation } from 'client/routes/navigation/PreventNavigation';
import { isOnlyCaptchaDirty } from '../../procedures/single/ProcedureFormPage';
import { useState } from 'react';
import { useRoleNetworkErrorMessage } from 'client/core/hooks/useRoleNetworkErrorMessage';
import { PageFullRow } from 'client/ui/layout/page/PageFullRow';
import { ProcedureFormMainColumn } from '../../procedures/single/shared/ProcedureFormMainColumn';
import { ProcedureFormSummaryColumn } from '../../procedures/single/shared/ProcedureFormSummaryColumn';
import { DesktopOnlyAfterContent } from '../../procedures/single/shared/DesktopOnlyAfterContent';
import { RequestCompileContextProvider } from 'client/components/schema/requests/compile-context/RequestCompileContextProvider';
import { useDraftSave } from '../../procedures/single/actions/useDraftSave';
import { ProcedureActionButtons } from '../../procedures/single/actions/ProcedureActionButtons';
import { RequestProcessingMessage } from 'client/components/schema/requests/waiting/RequestProcessingMessage';
import { RequestDetail } from 'client/components/schema/requests/detail/RequestDetail';

interface Params {
  id: string;
}

export interface RequestFormPageProps extends RouteComponentProps<Params> {
  // procedure: IProcedure
}

/**
 * Pagina di modifica di una richiesta. Da utilizzare quando la richiesta
 * è in bozza.
 */
export function RequestFormPage(props: RequestFormPageProps) {
  const layout = getWidgetInputLayout();
  const citizen = useCurrentCitizen();
  const user = useCurrentUser();

  const id = props.match.params.id ? parseInt(props.match.params.id, 10) : null;

  const { response, error, loading } = useApiQuery(RequestApi.find, {
    skip: id == null,
    data: {
      id: id!
    }
  });

  const request = response?.data;
  const procedure = request?.procedure;

  const [update, updateResponse] = useApiMutation(RequestApi.update, {
    invalidates: [RequestApi.find]
  });

  const [send, sendResponse] = useApiMutation(RequestApi.send, {
    invalidates: [RequestApi.find]
  });

  const [draftSave] = useDraftSave({
    procedure,
    run: async values =>
      await update({
        data: {
          id: request!.id,
          input: { ...values }
        }
      })
  });

  // const draftValidator = createDraftRequestValidator();

  // const draftOnClick = useCallback(
  //   async (formik: FormikProps<any>, id: number) => {
  //     const rawValues = formik.values;
  //     rawValues.sendRightNow = false;
  //     try {
  //       const values = await draftValidator.validate(rawValues, {
  //         abortEarly: false,
  //         context: {
  //           request: contextualRequest
  //         }
  //       });
  //       await update({
  //         data: {
  //           id,
  //           input: { ...values }
  //         }
  //       });
  //       message.success('Domanda salvata in bozza.');
  //     } catch (e) {
  //       // TODO Gestire. Caso di errore di validazione OPPURE errore nell'invio
  //       console.error('Errore di validazione non gestito', e);
  //     }
  //   },
  //   [update]
  // );

  const createAlertMessage = useRoleNetworkErrorMessage();

  useBreadcrumbItem({ path: '/richieste', title: 'Le mie Istanze' });
  useBreadcrumbItem({
    path: props.location.pathname,
    title: id ? request?.procedure.title ?? '...' : 'Modulo'
  });

  if (error)
    return (
      <AlertPage
        showIcon
        type="error"
        message="Impossibile caricare la domanda"
        description={getNetworkErrorMessage(error)}
      />
    );

  if (!request || !procedure || loading) return <LoadingPage />;

  const validator = createRequestValidator(procedure);

  const contextualRequest = ContextualRequestBag({
    citizen: citizen,
    sentAt: moment().toDate(),
    siap: user?.siap
  });

  if (
    request.state === RequestState.NeedRetry ||
    request.state === RequestState.Sending
  ) {
    return (
      <FrontLayout>
        <RequestProcessingMessage request={request} procedure={procedure} />
      </FrontLayout>
    );
  }

  return (
    <FrontLayout>
      <Formik<ICreateRequestDto>
        enableReinitialize
        validateOnMount
        initialValues={{ ...request, sendRightNow: false, captchaTokens: {} }}
        validate={schemaToValidator(validator, {
          context: {
            request: contextualRequest
          }
        })}
        onSubmit={async rawValues => {
          try {
            rawValues.sendRightNow = true;
            const values = await validator.validate(rawValues, {
              abortEarly: false,
              context: {
                request: contextualRequest
              }
            });
            await update({
              data: {
                id: request.id,
                input: { ...values }
              }
            });
            await send({ data: { id: request.id } });
            message.success('Domanda inviata con successo.');
          } catch (e) {
            // TODO Gestire. Caso di errore di validazione OPPURE errore nell'invio
            console.error('Errore di validazione non gestito', e);
          }
        }}
      >
        {formik => (
          <RequestCompileContextProvider
            procedure={procedure}
            request={formik.values}
          >
            <PreventNavigation
              when={
                formik.dirty &&
                !isOnlyCaptchaDirty(formik.values, formik.initialValues) &&
                !formik.isSubmitting &&
                !sendResponse.response
              }
            />
            <FormErrorsNotification />
            <FormikForm {...layout} disableEnterSubmit>
              {sendResponse.loading || sendResponse.response ? (
                <PageFullRow>
                  <Col span={24}>
                    <RequestSendSuccessMessage
                      procedure={procedure}
                      request={sendResponse.response?.data}
                      loading={sendResponse.loading}
                    />
                  </Col>
                </PageFullRow>
              ) : (
                <PageFullRow>
                  {/* Colonna principale */}
                  <ProcedureFormMainColumn
                    procedureTitle={procedure.title}
                    beforeContent={
                      sendResponse.error && (
                        <Alert
                          message="Errore nell'invio"
                          description={createAlertMessage(sendResponse.error)}
                          type="error"
                          showIcon
                          closable
                          style={{ marginBottom: '24px' }}
                        />
                      )
                    }
                    afterContent={
                      <DesktopOnlyAfterContent>
                        <ProcedureActionButtons
                          savedRequest={request}
                          draftSave={draftSave}
                          spaceProps={{
                            className: clx('desktop-only-after-content'),
                            direction: 'horizontal',
                            align: 'end',
                            size: 0,
                            style: { width: '100%' }
                          }}
                        />
                      </DesktopOnlyAfterContent>
                    }
                  />

                  {/* Colonna Sommario / Azioni */}
                  <ProcedureFormSummaryColumn procedure={procedure}>
                    {/* Pulsanti di Azione del Modulo della Domanda */}
                    <ProcedureActionButtons
                      savedRequest={request}
                      draftSave={draftSave}
                      spaceProps={{
                        direction: 'vertical',
                        size: 16,
                        style: { width: '100%' }
                      }}
                    />
                  </ProcedureFormSummaryColumn>
                </PageFullRow>
              )}
            </FormikForm>
          </RequestCompileContextProvider>
        )}
      </Formik>
    </FrontLayout>
  );
}
